import { useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { filter, find, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { NoStationAvailable } from '@/comp/common/styled';
import EPSelect, { EPSelectOption } from '../../components/EPSelect/EPSelect';
import { Select } from 'antd';
import { EPButton } from '@/components';
import { Container, StyledDivider, ButtonContainer, SelectPrefixStyle } from './EcoSelectStation.styles';
import { canShowStationPrefix, getTierTagPrefix } from './EcoSelectStation.utils';

const EcoSelectStation = observer(({ requiredPermission = null }) => {
  const { pathname } = useLocation();
  const { stationId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { stationStore, userStore, siteSurveyStore, permissionStore, pidEditStore } = useStores();
  const { stations, currentStation } = stationStore;
  const previousStationIdRef = useRef<string | null>(null);

  const stationToSelect = useMemo(() => {
    if (!requiredPermission) return stations;

    return filter(stations, station =>
      find(station.permissions, permission => {
        return permission.name === requiredPermission;
      }),
    );
  }, [stations, requiredPermission]);

  const sortedStationsForSelection = useMemo(() => {
    const stations = stationToSelect.map(s => {
      const customerName = userStore.showCaseEnabled ? 'Customer X' : s.customerName;

      return {
        id: s.id,
        stringToPresent: `${customerName} | ${s.siteName} | ${s.name}`,
        stationTier: s.stationTier,
        isFavorite: s.isFavorite,
        isShowcase: s.isShowcase,
      };
    });

    // Group stations by category
    const groupedStations = {
      favourites: stations.filter(s => s.isFavorite),
      showcase: stations.filter(s => s.isShowcase && !s.isFavorite),
      other: stations.filter(s => !s.isFavorite && !s.isShowcase),
    };

    // Sort each group alphabetically
    Object.keys(groupedStations).forEach(key => {
      groupedStations[key] = sortBy(groupedStations[key], ['stringToPresent']);
    });

    return groupedStations;
  }, [stationToSelect, userStore.showCaseEnabled]);

  const showStationPrefix = canShowStationPrefix(permissionStore);

  const optionsEl = useMemo(() => {
    const createStationOption = station => {
      const tierPrefix = getTierTagPrefix(station.stationTier, userStore.whiteLabelingTerms);
      const shouldShowPrefix = showStationPrefix && tierPrefix;

      return (
        <EPSelectOption key={station.id} value={station.id} label={station.stringToPresent}>
          <Popover
            placement="right"
            content={`${shouldShowPrefix ? tierPrefix + ' ' : ''}${station.stringToPresent}`}
            zIndex={10000}
            mouseEnterDelay={0.5}
          >
            <div className="option-container">
              {shouldShowPrefix && <span className="opt-prefix">{tierPrefix}</span>}
              <span className="station-name-container beggining-ellipsis">
                <span className="station-name-option">{station.stringToPresent}</span>
              </span>
            </div>
          </Popover>
        </EPSelectOption>
      );
    };

    return (
      <>
        {sortedStationsForSelection.favourites.length > 0 && (
          <Select.OptGroup label={t('favorite')}>
            {sortedStationsForSelection.favourites.map(createStationOption)}
          </Select.OptGroup>
        )}
        {sortedStationsForSelection.showcase.length > 0 && (
          <Select.OptGroup label={t('showcase')}>
            {sortedStationsForSelection.showcase.map(createStationOption)}
          </Select.OptGroup>
        )}
        {sortedStationsForSelection.other.length > 0 && (
          <Select.OptGroup label={t('allOthers')}>
            {sortedStationsForSelection.other.map(createStationOption)}
          </Select.OptGroup>
        )}
      </>
    );
  }, [sortedStationsForSelection, showStationPrefix, t, userStore.whiteLabelingAssets]);

  const unlockPreviousStationDraft = async () => {
    if (previousStationIdRef.current !== '') {
      await pidEditStore.disableEditModeLock(previousStationIdRef.current);
    }
  };

  const onChangeHandler = async value => {
    await unlockPreviousStationDraft();
    const newUrl = pathname.replace(stationId, value);
    previousStationIdRef.current = value;
    pidEditStore.setIsInEditMode(false);
    pidEditStore.toggleEditMode(false);
    navigate(newUrl);
  };

  const handleCreateNewSite = e => {
    e.stopPropagation();
    const newUrl = pathname + '?showSiteSurvey=true';
    navigate(newUrl);
  };

  const currentStationId = currentStation?.id || '';

  return (
    <Container className="eco-select-station">
      <SelectPrefixStyle />
      <EPSelect
        popupClassName="eco-select-station-popup"
        value={currentStationId}
        onChange={onChangeHandler}
        optionFilterProp="children"
        optionLabelProp="label"
        showSearch
        notFoundContent={<NoStationAvailable>{t('noStationsAvailable')}</NoStationAvailable>}
        filterOption={(input, option) => {
          if (!option || !option.label) {
            return false;
          }
          return option.label.toLowerCase().includes(input.toLowerCase());
        }}
        dropdownRender={menu => (
          <>
            {menu}
            {siteSurveyStore?.isUserEligibleToForm && (
              <>
                <StyledDivider />
                <ButtonContainer>
                  <EPButton type="link" onClick={handleCreateNewSite} size="middle" className="create-site-button">
                    {t('createNewSite', 'Create or Edit Site Survey')}
                  </EPButton>
                </ButtonContainer>
              </>
            )}
          </>
        )}
      >
        {optionsEl}
      </EPSelect>
    </Container>
  );
});

export default EcoSelectStation;
